import * as React from "react";
import { cn } from "@/lib/utils";

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  title?: string;
}

interface SegmentedProps<T extends string> {
  options: SegmentedOption<T>[];
  /** Selected option; a value not in `options` (e.g. "all") highlights none. */
  value: T | null | undefined;
  /** Called with the clicked option. Pages that toggle a filter off on a second click handle that here. */
  onChange: (value: T) => void;
  className?: string;
  "aria-label"?: string;
}

/** Filter / view switch shared by every list page: grey track, white pill for the active option, 32px tall. */
export function Segmented<T extends string>({ options, value, onChange, className, "aria-label": ariaLabel }: SegmentedProps<T>) {
  return (
    <div role="group" aria-label={ariaLabel} className={cn("inline-flex h-8 touch:h-10 shrink-0 items-center gap-0.5 rounded-lg bg-muted p-0.5", className)}>
      {options.map(o => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            title={o.title}
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex h-full flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 text-[13px] transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active ? "bg-background text-foreground font-medium shadow-sm" : "text-label hover:text-foreground",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
